import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

interface Toast { id: number; texte: string; erreur: boolean }
type Poser = (texte: string, erreur?: boolean) => void

const Ctx = createContext<Poser>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const seq = useRef(0)

  const poser = useCallback<Poser>((texte, erreur = false) => {
    const id = ++seq.current
    setToasts((t) => [...t.slice(-2), { id, texte, erreur }])
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), erreur ? 5200 : 2600)
  }, [])

  return (
    <Ctx.Provider value={poser}>
      {children}
      <div aria-live="polite" role="status">
        {toasts.map((t, i) => (
          <div key={t.id} className={`toast${t.erreur ? ' err' : ''}`}
               style={{ bottom: 18 + i * 38 }}>
            {t.texte}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export const useToast = (): Poser => useContext(Ctx)

/** Copie dans le presse-papier + confirmation — utilisé par l'étape 11. */
export function useCopier(): (valeur: string, libelle?: string) => void {
  const toast = useToast()
  return useCallback((valeur, libelle) => {
    void navigator.clipboard?.writeText(valeur)
      .then(() => toast(`COPIÉ — ${libelle ?? valeur}`))
      .catch(() => toast('COPIE REFUSÉE PAR LE NAVIGATEUR', true))
  }, [toast])
}

/** Ferme sur Échap — modales et panneau latéral. */
export function useEchap(actif: boolean, fermer: () => void): void {
  useEffect(() => {
    if (!actif) return
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') fermer() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [actif, fermer])
}
